import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, Globe } from 'lucide-react';
import { useSportsAuth, sportsApi } from '../SportsAuthContext';
import { AuthShell, SportsInput, SportsButton } from '../components/AuthShell';

function errText(detail) {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return detail?.msg || String(detail);
}

function slugify(name) {
  return (name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || '';
}

export default function SportsRegisterPage() {
  const { register } = useSportsAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: '', full_name: '', email: '', phone: '', password: '', subdomain: '' });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [subStatus, setSubStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid' | 'reserved'
  const [subEdited, setSubEdited] = useState(false);
  const debounceRef = useRef(null);

  const set = (k) => (e) => {
    const val = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: val };
      if (k === 'business_name' && !subEdited) {
        next.subdomain = slugify(val).slice(0, 30);
      }
      return next;
    });
  };

  const setSubdomain = (e) => {
    const raw = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 30);
    setSubEdited(true);
    setForm((f) => ({ ...f, subdomain: raw }));
  };

  const checkAvailability = useCallback(async (sub) => {
    if (!sub || sub.length < 3) { setSubStatus(sub ? 'invalid' : null); return; }
    setSubStatus('checking');
    try {
      const { data } = await sportsApi.get('/sports/subdomain-check', { params: { subdomain: sub } });
      setSubStatus(data.available ? 'available' : (data.reason || 'taken'));
    } catch {
      setSubStatus(null);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const sub = form.subdomain;
    if (!sub || sub.length < 3) { setSubStatus(sub ? 'invalid' : null); return; }
    debounceRef.current = setTimeout(() => checkAvailability(sub), 450);
    return () => clearTimeout(debounceRef.current);
  }, [form.subdomain, checkAvailability]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== confirmPassword) {
      setPasswordMismatch(true);
      return;
    }
    setPasswordMismatch(false);
    if (form.subdomain && subStatus !== 'available') {
      toast.error('Please choose an available subdomain.');
      return;
    }
    setBusy(true);
    try {
      await register(form);
      toast.success('Welcome to AgriBooks Sports!');
      navigate('/sports/dashboard');
    } catch (err) {
      toast.error(errText(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const statusUI = {
    checking: <Loader2 className="h-3.5 w-3.5 animate-spin text-[#A3A3A3]" />,
    available: <><CheckCircle className="h-3.5 w-3.5 text-[#00E676]" /><span className="text-[#00E676]">Available!</span></>,
    taken: <><XCircle className="h-3.5 w-3.5 text-[#FF2D55]" /><span className="text-[#FF2D55]">Already taken</span></>,
    reserved: <><XCircle className="h-3.5 w-3.5 text-[#FF2D55]" /><span className="text-[#FF2D55]">Reserved name</span></>,
    invalid: <><XCircle className="h-3.5 w-3.5 text-[#FFB300]" /><span className="text-[#FFB300]">Min 3 characters</span></>,
    invalid_format: <><XCircle className="h-3.5 w-3.5 text-[#FFB300]" /><span className="text-[#FFB300]">Invalid format</span></>,
  };

  return (
    <AuthShell
      title="Create Account"
      subtitle="Start your 15-day free trial — no approval needed."
      footer={
        <span>
          Already have an account?{' '}
          <Link to="/sports/login" className="text-[#E63946] font-semibold" data-testid="sports-go-login-link">
            Sign in
          </Link>
        </span>
      }
    >
      <form onSubmit={submit}>
        <SportsInput label="Business / Facility Name" testid="sports-register-business" value={form.business_name}
          onChange={set('business_name')} required placeholder="Smash Pickleball Center" />

        {/* Subdomain picker */}
        <label className="block mb-4">
          <span className="block text-xs uppercase tracking-wide text-[#A3A3A3] mb-1.5 flex items-center gap-1.5">
            <Globe className="h-3 w-3" /> Your Booking URL
            <span className="text-[#555] normal-case tracking-normal font-normal">(optional)</span>
          </span>
          <div className="flex items-center rounded-md overflow-hidden border border-[#262626] focus-within:border-[#E63946] focus-within:ring-1 focus-within:ring-[#E63946] transition-colors">
            <input
              data-testid="sports-register-subdomain"
              value={form.subdomain}
              onChange={setSubdomain}
              placeholder="your-facility"
              className="flex-1 bg-[#0A0A0A] text-white px-4 py-2.5 outline-none min-w-0"
            />
            <span className="text-xs text-[#555] px-3 py-2.5 bg-[#111] whitespace-nowrap select-none border-l border-[#262626]">.agri-books.com</span>
          </div>
          {subStatus && (
            <div className="flex items-center gap-1.5 mt-1.5 text-xs" data-testid="sports-register-subdomain-status">
              {statusUI[subStatus]}
            </div>
          )}
        </label>

        <SportsInput label="Your Name" testid="sports-register-name" value={form.full_name}
          onChange={set('full_name')} placeholder="Juan Dela Cruz" />
        <SportsInput label="Email" type="email" testid="sports-register-email" value={form.email}
          onChange={set('email')} required placeholder="you@court.com" />
        <SportsInput label="Phone" testid="sports-register-phone" value={form.phone}
          onChange={set('phone')} placeholder="09xx xxx xxxx" />
        <SportsInput label="Password" type="password" testid="sports-register-password" value={form.password}
          onChange={set('password')} required placeholder="At least 8 characters" />
        <SportsInput label="Confirm Password" type="password" testid="sports-register-confirm-password" value={confirmPassword}
          onChange={e => { setConfirmPassword(e.target.value); setPasswordMismatch(false); }} required placeholder="Repeat your password" />
        {passwordMismatch && (
          <p className="text-xs mb-3 -mt-2" style={{ color: '#E63946' }} data-testid="password-mismatch-error">
            Passwords do not match. Please try again.
          </p>
        )}
        <SportsButton type="submit" testid="sports-register-submit" disabled={busy || (form.subdomain && subStatus === 'checking')}>
          {busy ? 'Creating…' : 'Start Free Trial'}
        </SportsButton>
        <p className="text-center text-xs mt-3" style={{ color: '#555' }}>
          15-day free trial · No credit card required · Fully functional from day 1
        </p>
      </form>
    </AuthShell>
  );
}
